import React, { useRef, useState } from 'react';
import { useDrop } from 'react-dnd';
import { Textarea } from '@/components/ui/textarea';
import { DragItem } from '@/contexts/DragDropContext';
import { cn } from '@/lib/utils';
import { toast } from 'sonner';
import { ExpressionGenerator } from '@/lib/expression-generator';

interface DroppableTextareaProps extends React.TextareaHTMLAttributes<HTMLTextAreaElement> {
  value?: string;
  onChange?: (e: React.ChangeEvent<HTMLTextAreaElement>) => void;
  onValueChange?: (value: string) => void;
}

export const DroppableTextarea = React.forwardRef<HTMLTextAreaElement, DroppableTextareaProps>(
  ({ value, onChange, onValueChange, className, ...props }, ref) => {
    const textareaRef = useRef<HTMLTextAreaElement | null>(null);
    const [isDragging, setIsDragging] = useState(false);

    // Handle both onChange and onValueChange
    const handleChange = (e: React.ChangeEvent<HTMLTextAreaElement>) => {
      if (onChange) {
        onChange(e);
      }
      if (onValueChange) {
        onValueChange(e.target.value);
      }
    };

    const [{ isOver }, drop] = useDrop<DragItem, void, { isOver: boolean }>({
      accept: ['variable', 'schema-field'],
      drop: (item, monitor) => {
        const textarea = textareaRef.current;
        if (!textarea) return;

        const cursorPosition = textarea.selectionStart;
        const currentValue = value || '';
        
        // Determine what to insert based on the drag item type
        let insertText = '';
        if (item.type === 'schema-field') {
          // Use centralized ExpressionGenerator to handle proper bracket notation
          // for property names with spaces (e.g., "AI Agent" -> ["AI Agent"])
          const generated = ExpressionGenerator.generate(item);
          // Add spaces around the expression for readability in textareas
          insertText = generated.replace('{{', '{{ ').replace('}}', ' }}');
        } else if (item.type === 'variable') {
          // For variables, insert the label
          insertText = item.label || '';
        }
        
        const newValue =
          currentValue.slice(0, cursorPosition) +
          insertText +
          currentValue.slice(cursorPosition);

        // Update the value using onValueChange
        if (onValueChange) {
          onValueChange(newValue);
        }

        // Also trigger onChange with a synthetic event
        if (onChange) {
          const syntheticEvent = {
            target: { value: newValue },
            currentTarget: { value: newValue },
          } as React.ChangeEvent<HTMLTextAreaElement>;
          onChange(syntheticEvent);
        }

        // Set cursor position after the inserted text
        setTimeout(() => {
          textarea.selectionStart = textarea.selectionEnd =
            cursorPosition + insertText.length;
          textarea.focus();
        }, 0);

        toast.success(`Inserted ${item.type === 'schema-field' ? item.path : item.label}`);
      },
      collect: (monitor) => ({
        isOver: monitor.isOver(),
      }),
    });

    // Combine refs
    const setRefs = (element: HTMLTextAreaElement | null) => {
      textareaRef.current = element;
      drop(element);
      if (typeof ref === 'function') {
        ref(element);
      } else if (ref) {
        ref.current = element;
      }
    };

    return (
      <Textarea
        ref={setRefs}
        value={value}
        onChange={handleChange}
        className={cn(
          className,
          isOver && 'ring-2 ring-primary bg-primary/5 border-primary transition-all'
        )}
        {...props}
      />
    );
  }
);

DroppableTextarea.displayName = 'DroppableTextarea';
